import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { GraduationCap, LayoutDashboard, BookOpen, LogOut, Menu, X, Users, Shield, Settings, ClipboardList, AlertTriangle, CalendarDays, FileText, ShieldAlert, Phone } from 'lucide-react';
import { useSettings } from '@/contexts/SettingsContext';
import DataMeterChip from '@/components/DataMeterChip';

const Layout = ({ children, user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { settings } = useSettings();

  const adminNavItems = [
    { label: 'Dashboard', path: '/dashboard/admin', icon: Shield },
    { label: 'Configuración', path: '/admin/settings', icon: Settings },
  ];

  const teacherNavItems = [
    { label: 'Clases', path: '/classes', icon: BookOpen },
    ...(user.has_grupo_tutorado ? [{ label: 'Grupo Tutorado', path: '/teacher/grupo-tutorado', icon: ClipboardList }] : []),
    { label: 'Contraseña', path: '/teacher/settings', icon: Settings },
  ];

  const tutorNavItems = [
    { label: 'Dashboard', path: '/dashboard/tutor', icon: LayoutDashboard },
    { label: 'Resumen de Incumplimiento', path: '/tutor/resumen-incumplimiento', icon: AlertTriangle },
    { label: 'Actualizar Contacto', path: '/tutor/actualizar-contacto', icon: Phone },
  ];

  const controlEscolarNavItems = [
    { label: 'Gestión', path: '/dashboard/control-escolar', icon: Users },
    { label: 'Contacto Padres', path: '/control-escolar/contacto-padres', icon: Phone },
    { label: 'Fechas Parciales', path: '/control-escolar/fechas-parciales', icon: CalendarDays },
    { label: 'Alertas', path: '/alertas', icon: AlertTriangle, badge: true },
    { label: 'Contraseña', path: '/change-password', icon: Settings },
  ];

  const jefeDocentesNavItems = [
    { label: 'Dashboard', path: '/dashboard/jefe-docentes', icon: LayoutDashboard },
    { label: 'Acceso Profes.', path: '/teacher-access', icon: Users },
    { label: 'Contraseña', path: '/change-password', icon: Settings },
  ];

  const jefeTutoresNavItems = [
    { label: 'Dashboard', path: '/dashboard/jefe-tutores', icon: LayoutDashboard },
    { label: 'Acceso Tutores', path: '/ti-access-jt', icon: Users },
    { label: 'Contraseña', path: '/change-password', icon: Settings },
  ];

  const prefecturaNavItems = [
    { label: 'Prefectura', path: '/dashboard/prefectura', icon: ShieldAlert },
    { label: 'Contraseña', path: '/change-password', icon: Settings },
  ];

  const orientadorNavItems = [
    { label: 'Dashboard', path: '/dashboard/orientador', icon: LayoutDashboard },
    { label: 'Clases', path: '/orientador/clases', icon: BookOpen },
    { label: 'Historial Conducta', path: '/historial-conducta', icon: FileText },
    { label: 'Contacto Padres', path: '/orientador/contacto-padres', icon: Phone },
    { label: 'Alertas', path: '/alertas', icon: AlertTriangle, badge: true },
    { label: 'Contraseña', path: '/change-password', icon: Settings },
  ];

  const getNavItems = () => {
    switch (user.role) {
      case 'admin':
        return adminNavItems;
      case 'teacher':
        return teacherNavItems;
      case 'tutor':
        return tutorNavItems;
      case 'control_escolar':
        return controlEscolarNavItems;
      case 'jefe_docentes':
        return jefeDocentesNavItems;
      case 'jefe_tutores':
        return jefeTutoresNavItems;
      case 'orientador':
        return orientadorNavItems;
      case 'prefectura':
        return prefecturaNavItems;
      default:
        return [];
    }
  };

  const getRoleLabel = () => {
    switch (user.role) {
      case 'admin':
        return 'Administrador';
      case 'teacher':
        return 'Profesor';
      case 'tutor':
        return 'Tutor(a)';
      case 'control_escolar':
        return 'Control Escolar';
      case 'jefe_docentes':
        return 'Jefe de Docentes';
      case 'jefe_tutores':
        return 'Jefe de Tutores';
      case 'tutor_interno':
        return 'Tutor Interno';
      case 'prefectura':
        return 'Prefectura';
      default:
        return '';
    }
  };

  const navItems = getNavItems();

  // Logo component - shows custom logo or default icon
  const LogoDisplay = ({ size = 'normal' }) => {
    const sizeClasses = size === 'small' ? 'w-8 h-8' : 'w-10 h-10';
    const iconSize = size === 'small' ? 'w-5 h-5' : 'w-6 h-6';
    
    if (settings.logo_url) {
      return (
        <div className={`${sizeClasses} rounded-lg flex items-center justify-center overflow-hidden`}>
          <img src={settings.logo_url} alt={settings.app_name} className="w-full h-full object-contain" />
        </div>
      );
    }
    
    return (
      <div className={`${sizeClasses} bg-orange-500 rounded-lg flex items-center justify-center`}>
        <GraduationCap className={`${iconSize} text-white`} />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Sidebar Desktop */}
      <aside className="hidden md:flex md:flex-col w-64 bg-white border-r border-slate-200 sticky top-0 h-screen">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <LogoDisplay />
            <div>
              <h2 className="font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{settings.app_name}</h2>
              <p className="text-xs text-slate-600">{getRoleLabel()}</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto p-4 space-y-2">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid={`nav-${item.label.toLowerCase().replace('ó', 'o')}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-orange-50 text-orange-600 font-medium'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
                {item.badge && (
                  <span className="ml-auto w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" title="Alertas pendientes"></span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-200">
          {user.role === 'teacher' && (
            <div className="mb-3 flex justify-center" data-testid="data-meter-sidebar">
              <DataMeterChip />
            </div>
          )}
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center text-white font-semibold">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user.name}</p>
              <p className="text-xs text-slate-600 truncate">{user.email}</p>
            </div>
          </div>
          <Button
            onClick={onLogout}
            data-testid="logout-button"
            className="w-full bg-red-500 hover:bg-red-600 text-white font-medium px-4 py-2 rounded-lg"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-white border-r border-slate-200 flex flex-col">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <LogoDisplay />
                <div>
                  <h2 className="font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{settings.app_name}</h2>
                  <p className="text-xs text-slate-600">{getRoleLabel()}</p>
                </div>
              </div>
              <button onClick={() => setSidebarOpen(false)} className="text-slate-600 hover:text-slate-900">
                <X className="w-6 h-6" />
              </button>
            </div>

            <nav className="flex-1 p-4 space-y-2">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-orange-50 text-orange-600 font-medium'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </button>
                );
              })}
            </nav>

            <div className="p-4 border-t border-slate-200">
              <div className="flex items-center gap-3 mb-4 px-2">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center text-white font-semibold">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{user.name}</p>
                  <p className="text-xs text-slate-600 truncate">{user.email}</p>
                </div>
              </div>
              <Button
                onClick={onLogout}
                className="w-full bg-red-500 hover:bg-red-600 text-white font-medium px-4 py-2 rounded-lg"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Cerrar Sesión
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile Header */}
        <header className="md:hidden bg-white/80 backdrop-blur-md border-b border-slate-200/50 p-4 flex items-center justify-between sticky top-0 z-40">
          <button onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-button" className="text-slate-600 hover:text-slate-900">
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <LogoDisplay size="small" />
            <h2 className="font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{settings.app_name}</h2>
            {user.role === 'teacher' && <DataMeterChip className="hidden xs:inline-flex" />}
          </div>
          <button
            onClick={onLogout}
            data-testid="mobile-logout-button"
            aria-label="Cerrar sesión"
            className="flex items-center gap-1 px-2.5 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-md text-xs font-medium"
          >
            <LogOut className="w-3.5 h-3.5" />
            Salir
          </button>
        </header>

        <main className="flex-1 overflow-auto">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
